import { Widget } from "@foretagsplatsen/widgetjs";
import GroupedItemPicker from "../GroupedItemPicker/GroupedItemPicker.js";

/**
 * Renders a vertical list of items and an optional search field to
 * filter them. The user can click an item to select it.
 *
 * @param {object[]} [items] - The objects to render, one per
 * line. Each item can be of any type. If an item has a truthy
 * `selected` property, the item is considered selected. There must be
 * at most one such item. Items must have a `match()` method for the
 * search field to work. If the item has a `label` property and if no
 * `itemRenderer` is provided then this property must be a
 * renderable. If the item has a `value` attribute, then this
 * attribute is used by `selectValue()` and `getValue()`.
 * @param {function} [itemRenderer] - This function is passed an item
 * and must return a renderable. If not provided, the widget uses the
 * `label` property of the item is used to render it.
 * @param {bool} [liveSearch] - If truthy, the default, a search field
 * is displayed if the length of `items` is greater than
 * `liveSearchThreshold`. If falsy, no search field is displayed.
 * @param {integer} [liveSearchThreshold] - If the length of `items`
 * is greater than this value and if `liveSearch` is truthy, a
 * search field is rendered.
 * @param {bool} [standalone] - Set to true if not rendered as part of a
 * dropdown or other form control element.
 */
export default class ItemPicker extends Widget {
	constructor({
		items = [],
		itemRenderer,
		liveSearch,
		liveSearchThreshold,
		standalone = false,
	}) {
		super(...arguments);

		this._group = { items };

		this.selectionChanged = this._events.createEvent();

		this._groupedItemPicker = new GroupedItemPicker({
			groups: [this._group],
			itemRenderer,
			onSelectionChanged: (...rest) =>
				this.selectionChanged.trigger(...rest),
			liveSearch,
			liveSearchThreshold,
			cssClass: standalone ? "standalone" : "",
		});
	}

	getSelected() {
		return this._groupedItemPicker.getSelected();
	}

	getValue() {
		return this._groupedItemPicker.getValue();
	}

	getItems() {
		return this._group.items;
	}

	setItems(items) {
		this._group = { items };
		this._groupedItemPicker.setGroups([this._group]);
	}

	select(item) {
		return this._groupedItemPicker.select(item);
	}

	selectValue(value) {
		return this._groupedItemPicker.selectValue(value);
	}

	isSelected(item) {
		return this._groupedItemPicker.isSelected(item);
	}

	renderContentOn(html) {
		html.render(this._groupedItemPicker);
	}
}
